// Filter sidebar for Dashboard: date-range calendar + monthly todos

import React from 'react';
import { dateKey, parseDate } from '../data/slots.js';

function isoWeekNum(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}
function isoWeekStart(year, week) {
  // Monday of given ISO week
  const jan4 = new Date(year, 0, 4);
  const jan4Dow = (jan4.getDay() + 6) % 7;
  const week1Mon = new Date(year, 0, 4 - jan4Dow);
  const d = new Date(week1Mon);
  d.setDate(d.getDate() + (week - 1) * 7);
  return d;
}

function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtMD(d) { return `${d.getMonth() + 1}.${String(d.getDate()).padStart(2, '0')}`; }

function CalendarFilterPanel({ rangeStart, rangeEnd, onRangeChange, dataKeys, anchorDate, viewMonth: viewMonthProp, onViewMonthChange, collapsed: collapsedProp, onToggleCollapsed }) {
  // anchorDate: a Date determining which month to display; defaults to first data day or today
  // collapsed 可受控（Dashboard 让 月历 + 重点计划 联动折叠/展开）；未传则内部自治
  const [collapsedSelf, setCollapsedSelf] = React.useState(false);
  const collapsed = typeof collapsedProp === 'boolean' ? collapsedProp : collapsedSelf;
  const toggleCollapsed = () => { if (onToggleCollapsed) onToggleCollapsed(); else setCollapsedSelf(c => !c); };
  const initialMonth = anchorDate || (dataKeys[0] ? parseDate(dataKeys[0]) : new Date());
  // viewMonth：父组件可通过 prop 受控（Dashboard 用来同步 TodoPanel 的月份），否则内部自治
  const [viewMonthSelf, setViewMonthSelf] = React.useState(new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1));
  const viewMonth = viewMonthProp instanceof Date ? viewMonthProp : viewMonthSelf;
  const setViewMonth = (v) => {
    const next = typeof v === 'function' ? v(viewMonth) : v;
    if (onViewMonthChange) onViewMonthChange(next);
    else setViewMonthSelf(next);
  };
  const [pickStage, setPickStage] = React.useState('start'); // 'start' | 'end'

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();

  const startD = rangeStart ? parseDate(rangeStart) : null;
  const endD = rangeEnd ? parseDate(rangeEnd) : null;
  const startWeek = startD ? isoWeekNum(startD) : null;
  const endWeek = endD ? isoWeekNum(endD) : null;

  // build grid
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((firstDow + daysInMonth) / 7) * 7;
  const weeks = [];
  for (let i = 0; i < totalCells; i += 7) {
    const wk = [];
    for (let j = 0; j < 7; j++) {
      const cellIdx = i + j;
      const dayNum = cellIdx - firstDow + 1;
      if (dayNum < 1 || dayNum > daysInMonth) wk.push(null);
      else wk.push(new Date(year, month, dayNum));
    }
    weeks.push(wk);
  }

  function shiftMonth(delta) {
    setViewMonth(new Date(year, month + delta, 1));
  }

  function pickDay(d) {
    const k = dateKey(d);
    if (pickStage === 'start' || !rangeStart) {
      onRangeChange(k, k);
      setPickStage('end');
    } else {
      // second click — set end (swap if before start)
      if (k < rangeStart) onRangeChange(k, rangeStart);
      else onRangeChange(rangeStart, k);
      setPickStage('start');
    }
  }

  function inRange(d) {
    if (!startD || !endD) return false;
    const k = dateKey(d);
    return k >= dateKey(startD) && k <= dateKey(endD);
  }

  // Quick presets
  function preset(kind) {
    setPickStage('start');
    const allKeys = [...dataKeys].sort();
    if (kind === 'all') {
      if (allKeys.length) onRangeChange(allKeys[0], allKeys[allKeys.length - 1]);
      return;
    }
    if (kind === 'data') {
      if (allKeys.length) {
        onRangeChange(allKeys[0], allKeys[allKeys.length - 1]);
        setViewMonth(new Date(parseDate(allKeys[0]).getFullYear(), parseDate(allKeys[0]).getMonth(), 1));
      }
      return;
    }
    // "本月 / 本周 / 上周 / 近 7 日" 永远按真实今天算（不要被日表 anchorDate 污染 —— 用户日表停在 4 月，
    // 切到数据洞察点"本月"应该是真实今天所在月，不是 4 月）
    const today = new Date();
    if (kind === 'thisMonth') {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      const e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      onRangeChange(dateKey(s), dateKey(e));
      setViewMonth(s);
      return;
    }
    if (kind === 'thisWeek') {
      const dow = (today.getDay() + 6) % 7;
      const s = addDays(today, -dow);
      const e = addDays(s, 6);
      onRangeChange(dateKey(s), dateKey(e));
      return;
    }
    if (kind === 'lastWeek') {
      const dow = (today.getDay() + 6) % 7;
      const s = addDays(today, -dow - 7);
      const e = addDays(s, 6);
      onRangeChange(dateKey(s), dateKey(e));
      return;
    }
    if (kind === 'last7') {
      const e = today;
      const s = addDays(today, -6);
      onRangeChange(dateKey(s), dateKey(e));
      return;
    }
  }

  function setWeekRange(wStart, wEnd) {
    const s = isoWeekStart(year, wStart);
    const e = addDays(isoWeekStart(year, wEnd), 6);
    onRangeChange(dateKey(s), dateKey(e));
    setViewMonth(new Date(s.getFullYear(), s.getMonth(), 1));
  }

  return (
    <div className={`cal-panel ${collapsed ? 'collapsed' : ''}`}>
      <div className="cal-head">
        <button
          className="cat-collapse-btn"
          onClick={toggleCollapsed}
          title={collapsed ? '展开筛选' : '折叠筛选'}
        >{collapsed ? '›' : '‹'}</button>
        {!collapsed && (
          <React.Fragment>
            <button className="cal-nav" onClick={() => shiftMonth(-1)}>‹</button>
            <div className="cal-title">{year} 年 {month + 1} 月</div>
            <button className="cal-nav" onClick={() => shiftMonth(+1)}>›</button>
          </React.Fragment>
        )}
      </div>

      {collapsed ? (
        <div className="cal-rail-mini">
          <div className="cal-rail-mo">筛</div>
          <div className="cal-rail-wk">{startWeek ? `W${startWeek}` : '·'}</div>
        </div>
      ) : (
        <React.Fragment>
          <div className="filter-presets">
            <button className="fp-btn" onClick={() => preset('thisWeek')}>本周</button>
            <button className="fp-btn" onClick={() => preset('lastWeek')}>上周</button>
            <button className="fp-btn" onClick={() => preset('last7')}>近 7 日</button>
            <button className="fp-btn" onClick={() => preset('thisMonth')}>本月</button>
            <button className="fp-btn primary" onClick={() => preset('data')}>有数据</button>
          </div>

          <div className="filter-range-display">
            <div className="frd-block">
              <div className="frd-label">开始</div>
              <input
                type="date"
                className="frd-date"
                value={rangeStart || ''}
                onChange={e => onRangeChange(e.target.value, rangeEnd && e.target.value <= rangeEnd ? rangeEnd : e.target.value)}
              />
            </div>
            <div className="frd-sep">→</div>
            <div className="frd-block">
              <div className="frd-label">结束</div>
              <input
                type="date"
                className="frd-date"
                value={rangeEnd || ''}
                onChange={e => onRangeChange(rangeStart && e.target.value >= rangeStart ? rangeStart : e.target.value, e.target.value)}
              />
            </div>
          </div>

          {startWeek && endWeek && (
            <div className="cal-week-info">
              <span>第 <b>{startWeek}</b>{startWeek !== endWeek && <React.Fragment> – <b>{endWeek}</b></React.Fragment>} 周 / 52</span>
              <span className="cal-week-range">{startD && fmtMD(startD)} – {endD && fmtMD(endD)}</span>
            </div>
          )}

          <div className="filter-hint">
            {pickStage === 'start' ? '点击日历选择 ' : '再点一个日期作为 '}
            <b>{pickStage === 'start' ? '起始日' : '结束日'}</b>
          </div>

          <div className="cal-grid">
            <div className="cal-row cal-dow">
              {['一', '二', '三', '四', '五', '六', '日'].map((d, i) => (
                <div key={d} className={`cal-dow-cell ${i >= 5 ? 'weekend' : ''}`}>{d}</div>
              ))}
            </div>
            {weeks.map((wk, wi) => {
              // For each row, find leftmost and rightmost in-range cell to draw a continuous pill
              const inRangeMask = wk.map(d => (d ? inRange(d) : false));
              const firstIn = inRangeMask.indexOf(true);
              const lastIn = inRangeMask.lastIndexOf(true);
              return (
              <div key={wi} className="cal-row">
                {wk.map((d, di) => {
                  if (!d) return <div key={di} className="cal-cell empty" />;
                  const k = dateKey(d);
                  const hasData = dataKeys.includes(k);
                  const isStart = k === rangeStart;
                  const isEnd = k === rangeEnd;
                  const isIn = inRange(d);
                  const isWeekend = di >= 5;
                  let cls = 'cal-cell';
                  if (hasData) cls += ' has-data';
                  if (isIn) cls += ' in-range';
                  if (isIn && di === firstIn) cls += ' range-row-start';
                  if (isIn && di === lastIn) cls += ' range-row-end';
                  if (isStart) cls += ' range-start';
                  if (isEnd && !isStart) cls += ' range-end';
                  if (isWeekend) cls += ' weekend';
                  return (
                    <button
                      key={di}
                      className={cls}
                      onClick={() => pickDay(d)}
                      title={k}
                    >
                      <span className="cal-cell-num">{d.getDate()}</span>
                    </button>
                  );
                })}
              </div>
              );
            })}
          </div>

          <div className="cal-legend">
            <span className="cal-lg-item"><span className="dot has-data-dot"></span>已记录</span>
            <span className="cal-lg-item"><span className="dot in-range-dot"></span>选中区间</span>
            <span className="cal-lg-item"><span className="dot weekend-dot"></span>周末</span>
          </div>
        </React.Fragment>
      )}
    </div>
  );
}

function TodoPanel({ currentDate, collapsed: collapsedProp, onToggleCollapsed }) {
  const monthKey = `todos-${currentDate.getFullYear()}-${currentDate.getMonth() + 1}`;
  // collapsed 可受控（与月历联动）；未传则内部自治
  const [collapsedSelf, setCollapsedSelf] = React.useState(false);
  const collapsed = typeof collapsedProp === 'boolean' ? collapsedProp : collapsedSelf;
  const toggleCollapsed = () => { if (onToggleCollapsed) onToggleCollapsed(); else setCollapsedSelf(c => !c); };
  const [items, setItems] = React.useState(() => {
    try {
      const raw = localStorage.getItem(monthKey);
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    if (monthKey === 'todos-2026-4') {
      return [
        { id: 1, text: 'nas备份', done: false },
        { id: 2, text: '手机备份', done: false },
        { id: 3, text: '飞书学习', done: false },
        { id: 4, text: '找朴朴工作', done: false },
      ];
    }
    return [];
  });
  const [draft, setDraft] = React.useState('');

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(monthKey);
      if (raw) { setItems(JSON.parse(raw)); return; }
    } catch (e) {}
    if (monthKey === 'todos-2026-4') {
      setItems([
        { id: 1, text: 'nas备份', done: false },
        { id: 2, text: '手机备份', done: false },
        { id: 3, text: '飞书学习', done: false },
        { id: 4, text: '找朴朴工作', done: false },
      ]);
    } else {
      setItems([]);
    }
  }, [monthKey]);

  React.useEffect(() => {
    try { localStorage.setItem(monthKey, JSON.stringify(items)); } catch (e) {}
  }, [items, monthKey]);

  function addItem() {
    const t = draft.trim();
    if (!t) return;
    setItems([...items, { id: Date.now(), text: t, done: false }]);
    setDraft('');
  }
  function updateText(id, text) { setItems(items.map(it => it.id === id ? { ...it, text } : it)); }
  function toggle(id) { setItems(items.map(it => it.id === id ? { ...it, done: !it.done } : it)); }
  function remove(id) { setItems(items.filter(it => it.id !== id)); }

  const doneCount = items.filter(i => i.done).length;
  const monthLabel = `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}`;

  return (
    <div className={`todo-panel ${collapsed ? 'collapsed' : ''}`}>
      <div className="cal-head">
        <button
          className="cat-collapse-btn"
          onClick={toggleCollapsed}
          title={collapsed ? '展开计划' : '折叠计划'}
        >{collapsed ? '›' : '‹'}</button>
        {!collapsed && (
          <React.Fragment>
            <div className="cal-title todo-title">{monthLabel} 重点计划</div>
            <div className="todo-count">{doneCount}/{items.length}</div>
          </React.Fragment>
        )}
      </div>

      {collapsed ? (
        <div className="cal-rail-mini">
          <div className="cal-rail-mo">📋</div>
          <div className="cal-rail-wk">{doneCount}/{items.length}</div>
        </div>
      ) : (
        <React.Fragment>
          <div className="todo-list">
            {items.map((it, idx) => (
              <div key={it.id} className={`todo-row ${it.done ? 'done' : ''}`}>
                <button
                  className={`todo-check ${it.done ? 'on' : ''}`}
                  onClick={() => toggle(it.id)}
                  title="完成 / 未完成"
                />
                <span className="todo-idx">{idx + 1}.</span>
                <input
                  className="todo-text"
                  value={it.text}
                  onChange={e => updateText(it.id, e.target.value)}
                  placeholder="待办事项…"
                />
                <button className="todo-del" onClick={() => remove(it.id)} title="删除">×</button>
              </div>
            ))}
            {items.length === 0 && (
              <div className="todo-empty">本月暂无计划</div>
            )}
          </div>
          <div className="todo-add">
            <input
              className="todo-add-input"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addItem(); }}
              placeholder="+ 添加，回车确认"
            />
            <button className="todo-add-btn" onClick={addItem}>添加</button>
          </div>
        </React.Fragment>
      )}
    </div>
  );
}

export { CalendarFilterPanel, TodoPanel };
